/* eslint-disable camelcase */
import { BillingOpsPreviewView, ConvertToSalesOwnedInput } from './types'

export const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const POSITIVE_INT = /^[1-9]\d*$/

export interface BulkLine {
  lineNumber: number
  enterpriseId: string
  input: ConvertToSalesOwnedInput
}

export interface BulkLineError {
  lineNumber: number
  message: string
}

/** Parses `enterprise_id, seats[, seat_cap]` lines (comma or tab separated); blank and `#` lines are skipped. */
export const parseBulkLines = (text: string): { lines: BulkLine[]; errors: BulkLineError[] } => {
  const lines: BulkLine[] = []
  const errors: BulkLineError[] = []
  const seen = new Set<string>()
  text.split(/\r?\n/).forEach((raw, idx) => {
    const lineNumber = idx + 1
    const trimmed = raw.trim()
    if (trimmed === '' || trimmed.startsWith('#')) return
    const [enterpriseId = '', seats = '', seatCap = '', ...rest] = trimmed.split(/[,\t]/).map((cell) => cell.trim())
    if (rest.some((cell) => cell !== '')) {
      errors.push({ lineNumber, message: 'Expected enterprise_id, seats, seat_cap' })
    } else if (!UUID_PATTERN.test(enterpriseId)) {
      errors.push({ lineNumber, message: `"${enterpriseId}" is not an enterprise id` })
    } else if (!POSITIVE_INT.test(seats)) {
      errors.push({ lineNumber, message: 'Seats must be a whole number above 0' })
    } else if (seatCap !== '' && !POSITIVE_INT.test(seatCap)) {
      errors.push({ lineNumber, message: 'Seat cap must be empty or a whole number above 0' })
    } else if (seen.has(enterpriseId.toLowerCase())) {
      errors.push({ lineNumber, message: 'Duplicate enterprise id' })
    } else {
      seen.add(enterpriseId.toLowerCase())
      lines.push({
        lineNumber,
        enterpriseId,
        input: { seats: Number(seats), seat_cap: seatCap === '' ? null : Number(seatCap) },
      })
    }
  })
  return { lines, errors }
}

export type BulkRowStatus = 'pending' | 'previewed' | 'committed' | 'failed'

export interface BulkRow extends BulkLine {
  status: BulkRowStatus
  costCenterId: string | null
  preview: BillingOpsPreviewView | null
  error: string | null
  idempotencyRoot: string
}

/** Rows that previewed cleanly and resolved a cost center; only these are committed. */
export const rowsToCommit = (rows: BulkRow[]): BulkRow[] =>
  rows.filter((row) => row.status === 'previewed' && row.costCenterId !== null)

export const csvCell = (value: string): string => (/[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value)

/** One CSV row per enterprise with the Stripe objects Support must clean up by hand. */
export const bulkRowsToCsv = (rows: BulkRow[]): string => {
  const header = ['enterprise_id', 'cost_center_id', 'status', 'error', 'stripe_objects_for_support']
  const body = rows.map((row) =>
    [
      row.enterpriseId,
      row.costCenterId ?? '',
      row.status,
      row.error ?? '',
      (row.preview?.customer_impact.artifacts ?? [])
        .filter((a) => a.stripe_id)
        .map((a) => `${a.kind}:${a.stripe_id}`)
        .join(' '),
    ]
      .map(csvCell)
      .join(',')
  )
  return [header.join(','), ...body].join('\n')
}
